import type {
  CourseAssignmentResponse,
  CourseClassroomResponse,
  CourseDefinitionContentRequest,
  CourseDefinitionContentResponse,
  CourseDefinitionDemonstrationResponse,
  CourseDefinitionLessonResponse,
  CourseDefinitionQuizQuestionAnswerResponse,
  CourseDefinitionQuizQuestionResponse,
  CourseDefinitionQuizResponse,
  CourseDefinitionRequest,
  CourseDefinitionResponse,
  CourseDefinitionSafeResponse,
} from 'src/api/ProactApiClient';
import {
  CourseDefinitionContentTypeEnum,
  CourseDefinitionQuizQuestionTypeEnum,
  VideoProviderTypeEnum,
} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2} from 'src/types/FormV2';
import {emptyToUndefined} from 'src/types/FormV2Mapping';
import {v4 as uuidv4} from 'uuid';

// UI-only discriminator; fixed-date mode is derived from field presence
// server-side.
export const RENEWAL_SCHEDULE = {RELATIVE: '1', FIXED: '2'} as const;

const deriveRenewalScheduleType = (fixedRenewalMonth?: number | null): string =>
  fixedRenewalMonth != null
    ? RENEWAL_SCHEDULE.FIXED
    : RENEWAL_SCHEDULE.RELATIVE;

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const CreateDefaultCourseClassroom = (
  definitionId?: number
): CourseClassroomResponse => {
  return {
    id: undefined,
    courseDefinitionId: definitionId,
    attendees: [],
    classroomAt: new Date(),
  } as CourseClassroomResponse;
};

export const CreateDefaultCourseAssignment = (
  definitionId?: string
): CourseAssignmentResponse => {
  return {
    id: undefined,
    active: true,
    assignedToId: undefined,
    courseDefinitionId: definitionId,
  } as CourseAssignmentResponse;
};

export const CreateDefaultCourseDefinition = (): CourseDefinitionResponse => {
  return {
    locked: false,
    invariantId: uuidv4(),
    name: '',
    defaultLanguage: 'en',
    availableLanguages: ['en'],
    isRenewalRequired: false,
    renewalPeriodDays: 0,
    // Form-local schedule-mode toggle; never sent to the API.
    renewalScheduleType: '1',
    fixedRenewalMonth: undefined,
    fixedRenewalDay: undefined,
    isAttemptLimited: false,
    attemptLimit: 0,
    attemptLockoutPeriodDays: 0,
    isCertificateGenerated: false,
    certificateAdditionalContent: undefined,

    contents: [CreateDefaultCourseDefinitionContent()],
    dependencies: [],
    // sourceEntities is a legacy, read-only field. Never populated or
    // submitted from the edit form.
    sourceEntities: [],
  } as CourseDefinitionResponse;
};

export const CreateDefaultCourseDefinitionLesson =
  (): CourseDefinitionLessonResponse => {
    return {
      headerText: '',
      bodyText: '',
      videoProviderType: VideoProviderTypeEnum.Vimeo,
      videoUrl: '',
      enableVideoActiveScreenMonitoring: true,
      enableVideoControls: false,
      enableVideoAutostart: false,
      enableVideoResume: true,
    } as CourseDefinitionLessonResponse;
  };

export const CreateDefaultCourseDefinitionQuiz =
  (): CourseDefinitionQuizResponse => {
    return {
      headerText: '',
      bodyText: '',
      scorePassPercentage: 50,
      enableGenerateCertificate: true,
    } as CourseDefinitionQuizResponse;
  };

export const CreateDefaultCourseDefinitionDemonstration =
  (): CourseDefinitionDemonstrationResponse => {
    return {
      headerText: '',
      bodyText: '',
      assignToUserId: undefined,
      demonstrationFormDefinitionId: undefined,
      demonstrationFormDefinitionVersion: undefined,
      skillId: undefined,
      enableFailOnNotYetCompetent: false,
    } as CourseDefinitionDemonstrationResponse;
  };

export const CreateDefaultCourseDefinitionContent =
  (): CourseDefinitionContentResponse => {
    return {
      order: 0,
      invariantId: uuidv4(),
      language: 'en',
      type: CourseDefinitionContentTypeEnum.Lesson,
      timeLimit: 60,
      lesson: CreateDefaultCourseDefinitionLesson(),
      quiz: CreateDefaultCourseDefinitionQuiz(),
      demonstration: CreateDefaultCourseDefinitionDemonstration(),
    } as CourseDefinitionContentResponse;
  };

/**
 * Quiz questions have no server-assigned invariant identifier (unlike
 * contents), so brand-new questions need a client-minted stable key to
 * survive reorders/edits without the outline's selection following array
 * position. Only present on questions that haven't been saved yet — once
 * persisted, `id` is the stable key (see `getQuestionKey`).
 */
export type QuizQuestionWithKey = CourseDefinitionQuizQuestionResponse & {
  clientKey?: string;
};

export const getQuestionKey = (question: QuizQuestionWithKey): string =>
  question.id !== undefined ? `q-${question.id}` : `q-${question.clientKey}`;

export const CreateDefaultCourseDefinitionQuizQuestion =
  (): QuizQuestionWithKey => {
    return {
      order: 0,
      type: CourseDefinitionQuizQuestionTypeEnum.SingleChoice,
      labelText: '',
      bodyText: '',
      scoreValue: 1,
      answers: [],
      clientKey: uuidv4(),
    };
  };

export const CreateDefaultCourseDefinitionQuizQuestionAnswer = (
  order: number
): CourseDefinitionQuizQuestionAnswerResponse => {
  return {
    order: order,
    text: '',
    isCorrect: false,
  } as CourseDefinitionQuizQuestionAnswerResponse;
};

export const MapCourseFormDataToCourseDefinitionRequest = (
  data: IFormDataV2,
  contents: CourseDefinitionContentResponse[]
): CourseDefinitionRequest => {
  // Only send the two ints when the form is in fixed-date mode.
  const isFixedRenewal = data.renewalScheduleType === RENEWAL_SCHEDULE.FIXED;
  return {
    id: data.id,
    version: data.version,
    name: data.name,
    active: data.active,
    defaultLanguage: data.defaultLanguage,
    dueInDays: emptyToUndefined(data.dueInDays) as number | undefined,
    isRenewalRequired: data.isRenewalRequired,
    renewalPeriodDays: data.renewalPeriodDays || 0,
    fixedRenewalMonth: isFixedRenewal
      ? Number(data.fixedRenewalMonth) || undefined
      : undefined,
    fixedRenewalDay: isFixedRenewal
      ? Number(data.fixedRenewalDay) || undefined
      : undefined,
    isAttemptLimited: data.isAttemptLimited,
    attemptLimit: data.attemptLimit || 0,
    attemptLockoutPeriodDays: data.attemptLockoutPeriodDays || 0,
    isCertificateGenerated: data.isCertificateGenerated,
    certificateAdditionalContent: data.certificateAdditionalContent,
    certifyingTrainerName: data.certifyingTrainerName,
    certifyingTrainerTitle: data.certifyingTrainerTitle,
    targetDependencies: data.targetDependencies?.map(
      (dependency: ISelectOption) => Number(dependency.id)
    ),
    targetContents: contents.map((content, index) =>
      MapContentResponseToContentRequest(content, index)
    ),
    // targetSourceEntityRequest is required by the DTO but ignored
    // server-side; always send an empty payload.
    targetSourceEntityRequest: {entities: []},
    projectId: emptyToUndefined(data.projectId) as number | undefined,
  } as CourseDefinitionRequest;
};

export const MapContentResponseToContentRequest = (
  data: CourseDefinitionContentResponse,
  order: number
): CourseDefinitionContentRequest => {
  return {
    invariantId: data.invariantId,
    order,
    language: data.language,
    type: data.type,
    timeLimit: data.timeLimit,
    lesson: data.lesson,
    quiz: data.quiz && {
      ...data.quiz,
      questions: data.quiz.questions?.map((question, questionIndex) => {
        const {clientKey: _clientKey, ...rest} =
          question as QuizQuestionWithKey;
        return {
          ...rest,
          order: questionIndex,
          answers: question.answers?.map((answer, answerIndex) => ({
            ...answer,
            order: answerIndex,
          })),
        };
      }),
    },
    demonstration: data.demonstration,
  } as CourseDefinitionContentRequest;
};

export const MapCourseDefinitionResponseToCourseFormData = (
  data: CourseDefinitionResponse
): IFormDataV2 => {
  return {
    id: data.id,
    version: data.version,
    name: data.name,
    active: data.active,
    defaultLanguage: data.defaultLanguage,
    dueInDays: data.dueInDays,
    isRenewalRequired: data.isRenewalRequired,
    renewalPeriodDays: data.renewalPeriodDays,
    fixedRenewalMonth:
      data.fixedRenewalMonth != null
        ? String(data.fixedRenewalMonth)
        : undefined,
    fixedRenewalDay: data.fixedRenewalDay ?? undefined,
    renewalScheduleType: deriveRenewalScheduleType(data.fixedRenewalMonth),
    isAttemptLimited: data.isAttemptLimited,
    attemptLimit: data.attemptLimit,
    attemptLockoutPeriodDays: data.attemptLockoutPeriodDays,
    isCertificateGenerated: data.isCertificateGenerated,
    certificateAdditionalContent: data.certificateAdditionalContent,
    certifyingTrainerName: data.certifyingTrainerName,
    certifyingTrainerTitle: data.certifyingTrainerTitle,
    updatedAt: data.updatedAt,
    targetDependencies: data.dependencies ?? [],
    targetContents: [],
    projectId: data.projectId,
    project: data.project,
  } as IFormDataV2;
};

export const MapCourseDefinitionSafeResponseToCourseFormData = (
  data: CourseDefinitionSafeResponse
): IFormDataV2 => {
  return {
    id: data.id,
    version: data.version,
    name: data.name,
    active: data.active,
    isRenewalRequired: data.isRenewalRequired,
    renewalPeriodDays: data.renewalPeriodDays,
    fixedRenewalMonth:
      data.fixedRenewalMonth != null
        ? String(data.fixedRenewalMonth)
        : undefined,
    fixedRenewalDay: data.fixedRenewalDay ?? undefined,
    renewalScheduleType: deriveRenewalScheduleType(data.fixedRenewalMonth),
    isAttemptLimited: data.isAttemptLimited,
    attemptLimit: data.attemptLimit,
    attemptLockoutPeriodDays: data.attemptLockoutPeriodDays,
    isCertificateGenerated: data.isCertificateGenerated,
    certificateAdditionalContent: data.certificateAdditionalContent,
    certifyingTrainerName: data.certifyingTrainerName,
    certifyingTrainerTitle: data.certifyingTrainerTitle,
    updatedAt: data.updatedAt,
  } as IFormDataV2;
};
